import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PDFObject } from 'react-pdfobject';
import { useHistory } from 'react-router';
import {
    AppBar,
    Dialog,
    Icon,
    IconButton,
    Toolbar,
    Typography,
} from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';

import { useDownloadFileMutation } from '../rtkApi';
import './FileDownload.scss';

interface IProps {
    readonly path?: string;
    readonly name?: string;
    readonly preview?: boolean;
    readonly stopTableClickPropagation?: boolean;
    fileContentField?: string;
    queryParams?: object;
    children?: JSX.Element;
}

export const FileDownload = ({
    path,
    name,
    fileContentField,
    queryParams,
    preview = false,
    stopTableClickPropagation,
    children,
}: IProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [shouldOpenNewWindow, setShouldOpenNewWindow] = useState(false);

    const history = useHistory();

    const [
        downloadFile,
        {
            data: downloadData,
            isLoading: loading,
            isError: downloadError,
            isSuccess: downloadSuccess,
        },
    ] = useDownloadFileMutation();

    useEffect(() => {
        if (loading) {
            showToast({ body: t('file.downloading'), type: 'info' });
            return;
        }
        if (downloadError) {
            showToast({ body: t('file.downloadFailed'), type: 'error' });
            return;
        }
        if (downloadSuccess) {
            showToast({
                body: t('file.downloadSuccess'),
                type: 'success',
            });
        }
    }, [downloadError, downloadSuccess, loading]);

    const fileContent = downloadData?.fileContent;
    const filePreviewUrl = downloadData?.filePreviewUrl;

    const handleClick =
        (shouldPreview: boolean, shouldStopPropagation?: boolean) =>
        (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
            event.preventDefault();
            const filePath = path;
            const fileName = name;

            if (!filePath || !fileName) return;

            if (shouldStopPropagation) {
                event.stopPropagation();
            }
            setShouldOpenNewWindow(shouldPreview);
            downloadFile({
                fileContentField,
                queryParams,
                filePath,
                fileName,
                filePreview: shouldPreview,
            });
        };

    const closePreviewClick = () => {
        history.goBack();
        setOpen(false);
    };

    useEffect(() => {
        if (history.action === 'POP' && !!open) {
            setOpen(false);
        }
    }, [history.action, open]);

    useEffect(() => {
        if (filePreviewUrl && shouldOpenNewWindow) {
            window.open(filePreviewUrl, 'preview');
            setShouldOpenNewWindow(false);
        }
    }, [filePreviewUrl, shouldOpenNewWindow]);

    if (loading) {
        return <div>{children}</div>;
    }

    return (
        <>
            {preview && (
                <div onClick={handleClick(true, stopTableClickPropagation)}>
                    {children}
                </div>
            )}
            {!preview && (
                <div onClick={handleClick(false, stopTableClickPropagation)}>
                    {children}
                </div>
            )}

            {fileContent && open && (
                <Dialog fullScreen={true} open={open} onClose={() => ({})}>
                    <AppBar>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                onClick={closePreviewClick}
                                aria-label="Close"
                            >
                                <Icon>close</Icon>
                            </IconButton>
                            <Typography
                                variant="h6"
                                color="inherit"
                                className="FilePreview__Header"
                            >
                                {t('file.preview')}
                            </Typography>
                            <IconButton
                                color="inherit"
                                onClick={handleClick(
                                    true,
                                    stopTableClickPropagation,
                                )}
                            >
                                <Icon>save_alt</Icon>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    {filePreviewUrl && (
                        <PDFObject
                            containerId="FilePreview__File"
                            url={filePreviewUrl}
                        />
                    )}
                </Dialog>
            )}
        </>
    );
};
